import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { SystemURL } from "./URLCatalog";

export interface PilotCatalogAnalytics {
    ticketsLast2Weeks: number;
    ticketsLastWeek: number;
    ticketsPreviousWeek: number;
    usersLast2Weeks: number;
    usersLastWeek: number;
    usersPreviousWeek: number;
    startTicketTimestamp: number | null;
    lastTicketTimestamp: number | null;
}

export interface PilotCatalogEntry {
    organizationId: string;
    name: string;
    type: 'Pilot';
    countryCode: string;
    timezone: string;
    customer: string;
    usersLimit: number | string;
    legalName: string;
    startDate: string;
    endDate: string;
    creationTimestamp: number;
    analytics: PilotCatalogAnalytics;
    organizationType?: string;
    pilotSource?: string;
}

export type PilotOrganizationSetup = 'presenteeismAndPayslips' | 'presenteeism' | 'payslips';

export type PilotSourceOption =
    | 'instagram'
    | 'mailOutbound'
    | 'partnerPeru'
    | 'partnerUruguay'
    | 'partnerGuatemala'
    | 'partnerThompsonReuters'
    | 'press'
    | 'google'
    | 'linkedin'
    | 'other';

export interface CreatePilotPayload {
    organizationID: string;
    countryCode: string;
    name: string;
    adminEmail?: string;
    adminName?: string;
    buildingName: string;
    latitude: string;
    longitude: string;
    organizationSetup: PilotOrganizationSetup;
    pilotSource: PilotSourceOption;
}

export interface DeprecatePilotPayload {
    organizationID: string;
    deprecatedBy?: string;
    reason?: string;
    deleteExternalJobs?: boolean;
}

export interface ConvertPilotPayload {
    organizationID: string;
    convertedBy?: string;
    reason?: string;
}

export interface UpdatePilotPayload {
    organizationID: string;
    name?: string;
    countryCode?: string;
    timezone?: string;
}

const DEFAULT_PILOT_BUILDING_NAME = 'Fancy building';
const DEFAULT_PILOT_LATITUDE = '-34.52010791066458';
const DEFAULT_PILOT_LONGITUDE = '-58.47592258972071';

const resolveQueueWorkerBaseURL = () => {
    const shouldUseLocalAPI = process.env.REACT_APP_USE_LOCAL_API === 'YES';
    const env = process.env.REACT_APP_FRONT_ENV || 'PROD';

    if (shouldUseLocalAPI) {
        return 'http://localhost:8081';
    }

    switch (env) {
        case 'QA':
            return 'https://qa-analytics.qeeptouch.com';
        case 'PROD':
        default:
            return 'https://analytics.qeeptouch.com';
    }
};

const buildQueueWorkerURL = (urlTemplate: string, variables: Record<string, string>) => {
    const path = urlTemplate.replace(/:([A-Za-z0-9_]+)/g, (_, key: string) => {
        if (!(key in variables)) {
            throw new Error(`buildQueueWorkerURL: missing value for ":${key}"`);
        }

        return encodeURIComponent(variables[key]);
    });

    return `${resolveQueueWorkerBaseURL()}${path}`;
};

export class PilotOnboardingService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getPilots(): Promise<PilotCatalogEntry[]> {
        const request = new HTTPRequest();
        request.url = buildQueueWorkerURL(SystemURL.Onboarding.getPilots, {});
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        return (response.result ?? []) as PilotCatalogEntry[];
    }

    async createPilot(payload: CreatePilotPayload): Promise<string> {
        const includesQontactPoint = payload.organizationSetup !== 'payslips';
        const hasQontactPoint = Boolean(payload.buildingName.trim());
        const request = new HTTPRequest();
        request.url = buildQueueWorkerURL(SystemURL.Onboarding.postCreatePilot, {
            org_id: payload.organizationID.trim().toUpperCase(),
        });
        request.method = "POST";
        request.body = {
            countryCode: payload.countryCode.trim().toUpperCase(),
            name: payload.name.trim(),
            adminEmail: payload.adminEmail?.trim() || undefined,
            adminName: payload.adminName?.trim() || undefined,
            organizationSetup: payload.organizationSetup,
            pilotSource: payload.pilotSource,
            ...(includesQontactPoint ? {
                buildingName: hasQontactPoint
                    ? payload.buildingName.trim()
                    : DEFAULT_PILOT_BUILDING_NAME,
                latitude: hasQontactPoint
                    ? payload.latitude.trim()
                    : DEFAULT_PILOT_LATITUDE,
                longitude: hasQontactPoint
                    ? payload.longitude.trim()
                    : DEFAULT_PILOT_LONGITUDE,
            } : {}),
        };

        const response = await this.httpClient.execute(request);
        return response.result as string;
    }

    async updatePilot(payload: UpdatePilotPayload): Promise<void> {
        const request = new HTTPRequest();
        request.url = buildQueueWorkerURL(SystemURL.Onboarding.patchUpdatePilotOrganization, {
            org_id: payload.organizationID.trim().toUpperCase(),
        });
        request.method = "PATCH";
        request.body = {
            name: payload.name,
            countryCode: payload.countryCode,
            timezone: payload.timezone,
        };

        await this.httpClient.execute(request);
    }

    async convertPilot(payload: ConvertPilotPayload): Promise<void> {
        const request = new HTTPRequest();
        request.url = buildQueueWorkerURL(SystemURL.Onboarding.postConvertPilotOrganization, {
            org_id: payload.organizationID.trim().toUpperCase(),
        });
        request.method = "POST";
        request.body = {
            convertedBy: payload.convertedBy,
            reason: payload.reason,
        };

        await this.httpClient.execute(request);
    }

    async deprecatePilot(payload: DeprecatePilotPayload): Promise<void> {
        const request = new HTTPRequest();
        request.url = buildQueueWorkerURL(SystemURL.Onboarding.postDeprecatePilotOrganization, {
            org_id: payload.organizationID.trim().toUpperCase(),
        });
        request.method = "POST";
        request.body = {
            deprecatedBy: payload.deprecatedBy,
            reason: payload.reason,
            deleteExternalJobs: payload.deleteExternalJobs,
        };

        await this.httpClient.execute(request);
    }
}
